import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  authApiClient,
  cxApiClient,
  mxApiClient,
  onboardingApiClient,
  wxApiClient,
} from "@shared/api/clients/apiClientFactory";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { transformJsonToObject } from "@shared/utils/jsonToObjectTransformer";
import {
  Choice,
  ConfigJson,
  ConsumerData,
  MeterDetail,
  MeterModel,
  ServiceData,
} from "../types";
import { MetersList } from "./components/Meters/types";
import {
  ActivityLogs,
  ConsumerAndMeterInfo,
  ConsumerSearch,
  ConsumptionGraphEntry,
  DeviceAlerts,
  DeviceAllocationEntry,
  DeviceMeterDetails,
  DeviceReadings,
  MeterNumber,
  MeterTypeCount,
  MxInventory,
  ServiceOrderList,
  WorkOrder,
} from "./types";

const getInventory = async ({
  remoteUtilityId,
  tab,
  page,
  limit,
  search,
  meter_type,
  manufacturer,
}: {
  remoteUtilityId: string;
  tab: string;
  page: number;
  limit: number;
  search?: string;
  meter_type?: any;
  manufacturer?: any;
}): Promise<MxInventory> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "inventory",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("tab", tab);
      query.push("page", page);
      query.push("limit", limit.toString());
      if (search && search !== undefined) {
        query.push("search", search);
      }
      if (meter_type) {
        query.push("meter_type", String(meter_type));
      }
      if (manufacturer) {
        query.push("manufacturer", String(manufacturer));
      }
    },
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<MxInventory>(response.data);
};

export const exportInventory = async ({
  remoteUtilityId,
  tab,
  page,
  limit,
  search,
  meter_type,
  manufacturer,
}: {
  remoteUtilityId: string;
  tab: string;
  page?: number;
  limit?: number;
  search?: string;
  meter_type?: any;
  manufacturer?: any;
}): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "inventory",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("tab", tab);

      if (page) query.push("page", page);
      if (limit) query.push("limit", limit.toString());

      if (search && search !== undefined) {
        query.push("search", search);
      }
      if (meter_type) {
        query.push("meter_type", String(meter_type));
      }
      if (manufacturer) {
        query.push("manufacturer", String(manufacturer));
      }

      query.push("export_format", "csv");
    },
  );

  const response = await mxApiClient.get(url);
  return response.data;
};

const getPremises = async (utilityId: string, code: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/territory_json",
    (query) => {
      query.push("remote_utility_id", utilityId);
      query.push("config_level", "premise");
      query.push("code_list", code);
    },
  );
  const response = await mxApiClient.get(url);
  return response.data.result ?? [];
};
export const getSubArea = async (code: string, utilityId: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    `config-map/territory_json`,
    (query) => {
      query.push("remote_utility_id", utilityId);
      query.push("config_level", "sub_area");
      query.push("code_list", code);
    },
  );
  const response = await mxApiClient.get(url);
  return response.data.result ?? [];
};

const getMxChoices = async (
  choice: string,
  remoteUtilityId?: string,
): Promise<Choice[]> => {
  const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
    "mx",
    "choices",
    [choice],
    (query) => {
      if (remoteUtilityId) {
        query.push("remote_utility_id", remoteUtilityId);
      }
    },
  );
  const response = await mxApiClient.get(url);
  return transformJsonToObject<Choice[]>(response.data.result);
};

const getPriorities = async (
  choice: string,
  remoteUtilityId?: string,
): Promise<Choice[]> => {
  const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
    "wx",
    "choices",
    [choice],
    (query) => {
      if (remoteUtilityId) {
        query.push("remote_utility_id", remoteUtilityId);
      }
    },
  );
  const response = await wxApiClient.get(url);
  return transformJsonToObject<Choice[]>(response.data.result);
};

const getServiceOrders = async ({
  remoteUtilityId,
  page,
  limit,
  status,
  searchData,
  created_date_gte,
  created_date_lte,
  operation_type,
}: {
  remoteUtilityId: string;
  page: number;
  limit: number;
  status?: string;
  searchData?: string;
  created_date_gte?: Date | string;
  created_date_lte?: Date | string;
  operation_type?: number;
}): Promise<ServiceOrderList> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "work-order-job-card",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("page", page.toString());
      query.push("limit", limit.toString());
      if (status) {
        query.push("status", status);
      }
      if (searchData) {
        query.push("search_data", searchData);
      }
      if (created_date_gte) {
        query.push("created_date_gte", created_date_gte.toString());
      }
      if (created_date_lte) {
        query.push("created_date_lte", created_date_lte.toString());
      }
      if (operation_type && operation_type >= 0) {
        query.push("operation_type", operation_type);
      }
    },
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<ServiceOrderList>(response.data);
};
const getServiceOrdersJobCard = async ({
  remoteUtilityId,
  searchData,
}: {
  remoteUtilityId: string;
  searchData?: string;
}): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "service-order-search",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      if (searchData) {
        query.push("search", searchData);
      }
    },
  );

  const response = await mxApiClient.get(url);
  return response.data.result ?? null;
};

const searchSubmeters = async ({
  remoteUtilityId,
  search,
  page,
  limit,
}: {
  remoteUtilityId: string;
  search?: string;
  page?: number;
  limit?: number;
}): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "master-meter/search-submeters",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      if (search) {
        query.push("search", search);
      }
      if (page) {
        query.push("page", page.toString());
      }
      if (limit) {
        query.push("limit", limit.toString());
      }
    },
  );

  const response = await mxApiClient.get(url);
  return response.data;
};
const searchMasterMeters = async ({
  remoteUtilityId,
  search,
}: {
  remoteUtilityId: string;
  search?: string;
}): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("is_master", true);
      if (search) {
        query.push("search", search);
      }
    },
  );

  const response = await mxApiClient.get(url);
  return response.data.results;
};

export const exportServiceOrders = async ({
  remoteUtilityId,
  page,
  limit,
  status,
  searchData,
  created_date_gte,
  created_date_lte,
  operation_type,
}: {
  remoteUtilityId: string;
  page?: number;
  limit?: number;
  status?: string;
  searchData?: string;
  created_date_gte?: Date | string;
  created_date_lte?: Date | string;
  operation_type?: number;
}): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "work-order-job-card",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);

      if (page) query.push("page", page.toString());
      if (limit) query.push("limit", limit.toString());

      if (status) {
        query.push("status", status);
      }
      if (searchData) {
        query.push("search_data", searchData);
      }
      if (created_date_gte) {
        query.push("created_date_gte", created_date_gte.toString());
      }
      if (created_date_lte) {
        query.push("created_date_lte", created_date_lte.toString());
      }
      if (operation_type && operation_type >= 0) {
        query.push("operation_type", operation_type);
      }

      query.push("export_format", "csv");
    },
  );

  const response = await mxApiClient.get(url);

  return response.data;
};

const getMeterModels = async ({
  remoteUtilityId,
  isActive = 1,
  utilityService,
  isDistinctMeterMakes,
  meterMake,
}: {
  remoteUtilityId: string;
  isActive?: number;
  utilityService?: string;
  isDistinctMeterMakes?: boolean;
  meterMake?: string;
}): Promise<MeterModel[]> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-model",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("is_active", isActive.toString());

      if (isDistinctMeterMakes) {
        query.push("is_distinct_meter_makes", isDistinctMeterMakes.toString());
      }

      if (meterMake) {
        query.push("meter_make", meterMake);
      }
      if (utilityService) {
        query.push("utility_service", utilityService);
      }
    },
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<MeterModel[]>(response.data.result);
};

const postBulkMeter = async (payload: FormData) => {
  const url = ApiEndpoints.createUrlWithPathVariables("mx", "inventory", [
    "meters",
    "bulk",
  ]);

  const response = await mxApiClient.postForm(url, payload);
  return response.data ?? null;
};

const getAllSOPs = async ({
  remoteUtilityId,
  operationType,
}: {
  remoteUtilityId: string;
  operationType: number;
}) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "get-all-sops",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("association_type", "METER");
      query.push("is_active", "true");
      query.push("operation_type", operationType);
    },
  );

  const response = await mxApiClient.get(url);
  return response.data ?? [];
};

const bulkCreateWorkOrderJobCard = async (payload: any) => {
  const url = ApiEndpoints.createUrl("mx", "bulk-create-work-order-job-card");
  const response = await mxApiClient.post(url, payload);
  return response.data ?? null;
};

// METERS API

export const getMeterSummary = async (remoteUtilityId: string) => {
  const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
    "mx",
    "meter",
    ["summary"],
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
    },
  );

  const response = await mxApiClient.get(url);
  return response.data.result ?? [];
};

export const getDeviceManagementMeters = async ({
  remoteUtilityId,
  page,
  limit,
  searchData,
  status,
  meterType,
}: {
  remoteUtilityId: string;
  page: number;
  limit: number;
  searchData?: string;
  status?: string;
  meterType?: any;
}): Promise<MetersList> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "device-management",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("search_data", searchData ?? "");
      query.push("page", page.toString());
      query.push("limit", limit.toString());

      if (status && status.toLowerCase() !== "all") {
        query.push("status", status.toUpperCase());
      }

      if (meterType && meterType !== "ALL") {
        query.push("meter_type", meterType);
      }
    },
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<MetersList>(response.data);
};

export const getAllMeters = async (params: {
  remoteUtilityId: string;
  page: number;
  limit: number;
  search?: string;
  status?: string;
  meter_type?: string; // ✅ snake_case
  manufacturer?: string;
  model?: string;
  service?: string;
  area?: string;
  subarea?: string;
  premise?: string;
  reading_status?: string; // ✅ snake_case
  date_from?: string; // ✅ snake_case
  date_to?: string; // ✅ snake_case
  operationType?: number;
}): Promise<MetersList> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "device-management",
    (query) => {
      query.push("remote_utility_id", params.remoteUtilityId);
      query.push("page", params.page.toString());
      query.push("limit", params.limit.toString());

      if (params.status && params.status.toLowerCase() !== "all") {
        query.push("status", params.status.toUpperCase());
      }
      if (params.search) {
        query.push("search_data", params.search);
      }

      if (params.meter_type) {
        query.push("meter_type", params.meter_type);
      }

      if (params.manufacturer) {
        query.push("manufacturer", params.manufacturer);
      }

      if (params.model) {
        query.push("model", params.model);
      }

      if (params.service) {
        query.push("service", params.service);
      }

      if (params.area) {
        query.push("area", params.area);
      }

      if (params.subarea) {
        query.push("subarea", params.subarea);
      }

      if (params.premise) {
        query.push("premise", params.premise);
      }

      if (params.reading_status) {
        query.push("reading_status", params.reading_status);
      }

      if (params.date_from) {
        query.push("date_from", params.date_from);
      }

      if (params.date_to) {
        query.push("date_to", params.date_to);
      }

      if (params.operationType) {
        query.push("operation_type", params.operationType.toString());
      }
    },
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<MetersList>(response.data);
};

export const exportDeviceManagementMeters = async ({
  remoteUtilityId,
  page,
  limit,
  searchData,
  status,
  meterType,
}: {
  remoteUtilityId: string;
  page?: number;
  limit?: number;
  searchData?: string;
  status?: string;
  meterType?: any;
}): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "device-management",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("search_data", searchData ?? "");

      if (page) query.push("page", page.toString());
      if (limit) query.push("limit", limit.toString());

      if (status && status.toLowerCase() !== "all") {
        query.push("status", status.toUpperCase());
      }

      if (meterType && meterType !== "ALL") {
        query.push("meter_type", meterType);
      }

      query.push("export_format", "csv");
    },
  );

  const response = await mxApiClient.get(url);
  return response.data;
};

export const downloadExcelSheet = async ({
  remoteUtilityId,
  fileName,
  meterId,
}: {
  remoteUtilityId: string;
  fileName: string;
  meterId?: number;
}) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "download-excel",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("file_name", fileName);
      if (meterId) {
        query.push("meter_id", meterId);
      }
    },
  );

  const response = await mxApiClient.get(url);
  return response.data;
};

const generateMeterNumber = async (
  remoteUtilityId: string,
): Promise<MeterNumber> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "generate-meter-number",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
    },
  );

  const response = await mxApiClient.get(url);
  const transformedResponse = transformJsonToObject<MeterNumber>(
    response.data.result,
  );
  return transformedResponse;
};

const getUtilityServices = async (
  utilityId: string,
): Promise<ServiceData[]> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "auth",
    "utility/services",
    (query) => {
      query.push("utility_id", utilityId);
    },
  );

  const response = await authApiClient.get(url);
  const tResponse = transformJsonToObject<ServiceData[]>(response.data.result);
  return tResponse;
};

const getConfigMapJson = async ({
  jsonType, // 'category_json' or 'territory_json'
  remoteUtilityId,
  configLevel,
  codeLevel,
}: {
  jsonType: "category_json" | "territory_json";
  remoteUtilityId: string;
  configLevel: string;
  codeLevel?: string;
}): Promise<ConfigJson[]> => {
  const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
    "onboarding",
    `config-map`,
    [jsonType],
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      // Skip config_level if it's "sub_category" but no codeLevel is present
      if (configLevel !== "sub_category" || codeLevel) {
        query.push("config_level", configLevel);
      }

      if (codeLevel) {
        query.push("code_list", codeLevel);
      }
    },
  );

  const response = await mxApiClient.get(url);
  const tResponse = transformJsonToObject<ConfigJson[]>(response.data.result);
  return tResponse;
};

const getMeterSearchPrompt = async ({
  remoteUtilityId,
  searchData,
  premise,
  productCode,
}: {
  remoteUtilityId: string;
  searchData: string;
  premise: string;
  productCode: string;
}): Promise<ConsumerSearch[]> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-search/prompt",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("search_field", "consumer_no");
      query.push("search_data", searchData);
      if (premise !== undefined) query.push("premise", premise);
      query.push("is_meter_reads", "0");
      query.push("status", "APPROVED");
      if (productCode !== undefined) query.push("product_code", productCode);
    },
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<ConsumerSearch[]>(response.data.result);
};

const createDevice = async (payload: any) => {
  const url = ApiEndpoints.createUrl("mx", "device-management");
  const response = await mxApiClient.post(url, payload);
  return response.data;
};

const getConsumerProfile = async ({
  remoteUtilityId,
  consumerNo,
}: {
  remoteUtilityId: string;
  consumerNo: string;
}): Promise<ConsumerData> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "consumer-profile",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("consumer_no", consumerNo);
    },
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<ConsumerData>(response.data.result);
};

const getMeterDetail = async ({
  meterId,
  remoteUtilityId,
}: {
  meterId: number;
  remoteUtilityId?: string;
}): Promise<MeterDetail> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-detail",
    (query) => {
      query.push("meter_id", meterId);
      query.push("remote_utility_id", remoteUtilityId);
    },
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<MeterDetail>(response.data.result);
};

const getDeviceDetail = async ({
  deviceId,
  remoteUtilityId,
}: {
  deviceId: number;
  remoteUtilityId: string;
}): Promise<DeviceMeterDetails> => {
  const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
    "mx",
    "device-detail",
    [deviceId],
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
    },
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<DeviceMeterDetails>(response.data.result);
};

// Add to your mxDeviceManagementApi
const getDeviceReadings = async ({
  meterId,
  searchData = "",
  page,
  pageSize,
  remoteUtilityId,
  fromDate,
  toDate,
  status,
}: {
  meterId: number;
  searchData?: string;
  page: number;
  pageSize: number;
  remoteUtilityId: string;
  fromDate?: string;
  toDate?: string;
  status?: string;
}): Promise<DeviceReadings> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "device-readings",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("meter_id", meterId);
      query.push("search_data", searchData);
      query.push("page", page);
      query.push("page_size", pageSize);

      // Add filter parameters only if they exist
      if (fromDate) query.push("from_date", fromDate);
      if (toDate) query.push("to_date", toDate);
      if (status && status !== "all") query.push("status", status);
    },
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<DeviceReadings>(response.data);
};

const getConsumptionGraph = async ({
  id,
  remoteUtilityId,
}: {
  id: number;
  remoteUtilityId: string;
}): Promise<ConsumptionGraphEntry[]> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "consumption-graph",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("meter_id", id);
    },
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<ConsumptionGraphEntry[]>(response.data.result);
};

export const getDeviceAllocationHistory = async ({
  meter_number,
  device_no,
  remoteUtilityId,
}: {
  meter_number: number;
  device_no: number;
  remoteUtilityId: string;
}): Promise<DeviceAllocationEntry[]> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "device-allocation-history",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("meter_number", meter_number);
      query.push("device_no", device_no);
    },
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<DeviceAllocationEntry[]>(response.data.result);
};

export const getAlerts = async ({
  meterId,
  remoteUtilityId,
}: {
  meterId: number;
  remoteUtilityId: string;
}): Promise<DeviceAlerts[]> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "device-alerts",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("meter_id", meterId);
    },
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<DeviceAlerts[]>(response.data.result);
};

export const getMeterWorkorderInfo = async ({
  meterId,
  remoteUtilityId,
}: {
  meterId: number;
  remoteUtilityId: string;
}): Promise<WorkOrder[]> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-workorder-info",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("meter_id", meterId);
    },
  );

  const response = await mxApiClient.get(url);
  return response.data.result;
};

const getActivityLogs = async ({
  meterId,
  remoteUtilityId,
}: {
  meterId: number;
  remoteUtilityId: string;
}): Promise<ActivityLogs[]> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "activityLog",
    "logs",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("meter_id", meterId);
      query.push("module", "mx");
    },
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<ActivityLogs[]>(response.data.results);
};

export const getMeterNotes = async ({
  meterId,
  remoteUtilityId,
}: {
  meterId: number;
  remoteUtilityId: string;
}) => {
  const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
    "mx",
    "meter",
    ["notes"],
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("meter_id", meterId);
    },
  );

  const response = await mxApiClient.get(url);
  return response.data.result;
};

export const postMeterNote = async (payload: any) => {
  const url = ApiEndpoints.createUrlWithPathAndQueryVariables("mx", "meter", [
    "notes",
  ]);
  const response = await mxApiClient.post(url, payload);
  return response.data;
};

const postWorkOrderJobCard = async (payload: any) => {
  const url = ApiEndpoints.createUrl("mx", "work-order-job-card");
  const response = await mxApiClient.post(url, payload);
  return response.data;
};

const patchMeterDetail = async ({
  deviceId,
  payload,
}: {
  deviceId: string;
  payload: any;
}): Promise<any> => {
  const url = ApiEndpoints.createUrlWithPathVariables(
    "mx",
    "device-management",
    [deviceId],
  );

  const response = await mxApiClient.patch(url, payload);
  return response?.data;
};

export const getMeterModel = async (
  remote_utility_id: string,
  meter_type: any,
  meter_manufacturer: any,
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-model",
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      query.push("meter_type", meter_type);
      query.push("meter_manufacturer", meter_manufacturer);
    },
  );

  const response = await mxApiClient.get(url);
  return response.data.result ?? [];
};

export const getManufacturers = async (remote_utility_id: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter/manufacturer",
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
    },
  );

  const response = await mxApiClient.get(url);
  return response.data.results ?? [];
};

export const getMeterManufacturers = async (params: {
  remote_utility_id: string;
}): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter/manufacturer",
    (qs) => {
      qs.push("remote_utility_id", params.remote_utility_id);
    },
  );
  const response = await mxApiClient.get(url);
  return response.data;
};

const getMeterTypeCount = async (
  remoteUtilityId: string,
): Promise<MeterTypeCount> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-type-count",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
    },
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<MeterTypeCount>(response.data.result);
};

const getUsedMeters = async (
  queryParams: Record<string, any> = {},
): Promise<any> => {
  const remoteUtilityId = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("status", "UNASSIGNED");
      query.push("physical_status", "UNINSTALLED");

      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          if (Array.isArray(value)) {
            value.forEach((v) => {
              if (v !== undefined && v !== null && v !== "") {
                query.push(key, v.toString());
              }
            });
          } else {
            query.push(key, value.toString());
          }
        }
      });
    },
  );

  const response = await mxApiClient.get(url);
  return response.data;
};

export const exportUsedMeters = async (
  queryParams: Record<string, any> = {},
): Promise<any> => {
  const remoteUtilityId = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("status", "UNASSIGNED");

      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          if (Array.isArray(value)) {
            value.forEach((v) => {
              if (v !== undefined && v !== null && v !== "") {
                query.push(key, v.toString());
              }
            });
          } else {
            query.push(key, value.toString());
          }
        }
      });

      query.push("export_format", "csv");
    },
  );

  const response = await mxApiClient.get(url);
  return response.data;
};

const getReasonChoices = async (): Promise<Choice[]> => {
  const remoteUtilityId = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithPathAndQueryVariables("mx", "choices", [
    "dispose_reason",
  ]);
  const response = await mxApiClient.get(url);
  return transformJsonToObject<Choice[]>(response.data.result);
};

const disposeMeters = async (payload: any) => {
  const url = ApiEndpoints.createUrlWithQueryParameters("mx", `meter/dispose`);
  const response = await mxApiClient.post(url, payload);
  return response.data?.result;
};

export const getMeterType = async () => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `choices/meter_type`,
  );
  const response = await mxApiClient.get(url);
  return response.data.result.map((item: any) => ({
    value: item.key,
    label: item.value,
  }));
};

const getSubMeterSummary = async (meterId: number, remoteUtilityId: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "master-meter/submeter-summary",
    (query) => {
      query.push("meter_id", meterId);
      query.push("remote_utility_id", remoteUtilityId);
    },
  );
  const response = await mxApiClient.get(url);
  return response.data.result;
};

const getSubMeterList = async ({
  meterId,
  remoteUtilityId,
  page,
  limit,
  search,
}: {
  meterId: number;
  remoteUtilityId: string;
  page?: number;
  limit?: number;
  search?: string;
}): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "master-meter/submeter-list",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("meter_id", meterId);
      if (search) query.push("search", search);
      if (page) query.push("page", page);
      if (limit) query.push("limit", limit);
    },
  );
  const response = await mxApiClient.get(url);
  return response.data;
};

const addSubMeter = async (payload: {
  master_meter_id: number;
  sub_meter_ids: number[];
  remote_utility_id: number;
}): Promise<any> => {
  const url = ApiEndpoints.createUrl("mx", "master-meter/add-submeter");
  const response = await mxApiClient.post(url, payload);
  return response.data;
};

export const mxDeviceManagementApi = {
  getSubMeterSummary,
  getSubMeterList,
  addSubMeter,
  getInventory,
  getMxChoices,
  getPremises,
  getSubArea,
  getServiceOrders,
  searchSubmeters,
  searchMasterMeters,
  getMeterModels,
  postBulkMeter,
  getAllSOPs,
  bulkCreateWorkOrderJobCard,
  getMeterSummary,
  getDeviceManagementMeters,
  generateMeterNumber,
  getUtilityServices,
  getConfigMapJson,
  getMeterSearchPrompt,
  createDevice,
  getConsumerProfile,
  getMeterDetail,
  getDeviceDetail,
  getDeviceReadings,
  getConsumptionGraph,
  getDeviceAllocationHistory,
  getAlerts,
  getMeterWorkorderInfo,
  getActivityLogs,
  getMeterNotes,
  postMeterNote,
  postWorkOrderJobCard,
  patchMeterDetail,
  getMeterModel,
  getManufacturers,
  getMeterManufacturers,
  getMeterTypeCount,
  getServiceOrdersJobCard,
  getUsedMeters,
  getReasonChoices,
  disposeMeters,
  getMeterType,
  exportDeviceManagementMeters,
  exportInventory,
  exportUsedMeters,
  exportServiceOrders,
  getAllMeters,
  getPriorities,
};
export const meterApi = {
  uploadReading: async (payload: any, id: any): Promise<any> => {
    const url = ApiEndpoints.createUrl("mx", `meters/${id}/readings`);
    const response = await mxApiClient.post(url, payload);
    return response.data;
  },

  //https://api-mx-mercury.bynry.com/api/mx/meter/assign-consumer/

  assignConsumer: async (payload: any): Promise<any> => {
    const url = ApiEndpoints.createUrl("mx", "meter/assign-consumer");
    const response = await mxApiClient.post(url, payload);
    return response.data;
  },
  getRoute: async (premise: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "mx",
      "route",
      (qs) => {
        qs.push("remote_utility_id", getRemoteUtilityId());
        qs.push("premise", premise);
      },
    );
    const response = await mxApiClient.get(url);
    return response.data;
  },

  getConsumers: async (
    page: any,
    limit: any,
    status: any,
    searchData?: string, // Add search parameter
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer",
      (qs) => {
        qs.push("remote_utility_id", getRemoteUtilityId());
        qs.push("page", page);
        qs.push("limit", limit);
        qs.push("status", status);

        // Only add search_data if it exists and is not empty
        if (searchData && searchData.trim()) {
          qs.push("search_data", searchData.trim());
        }
      },
    );
    const response = await cxApiClient.get(url);
    return response.data;
  },

  getPriority: async (): Promise<any> => {
    const url = ApiEndpoints.createUrl("wx", "choices/priority");
    const response = await wxApiClient.get(url);
    return response.data;
  },

  getPremise: async (
    page: any,
    limit: any,
    searchData?: string,
    excludeCodeName?: any,
  ): Promise<any> => {
    const REMOTE_UTILITY_ID = getRemoteUtilityId();
    const isSearch = !!searchData && searchData.trim() !== "";

    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      isSearch ? "search/onboarding_config" : "config-map/territory_json",
      (qs) => {
        // Common pagination params
        qs.push("remote_utility_id", REMOTE_UTILITY_ID);
        qs.push("limit", limit);
        qs.push("is_pagination_required", true);
        qs.push("page", page);
        qs.push("exclude_code_name", excludeCodeName);

        if (isSearch) {
          // Search-specific params
          qs.push("search_field", "territory_json-premise");
          qs.push("search_data", searchData!.trim());
        } else {
          // Non-search params
          qs.push("config_level", "premise");
        }
      },
    );

    const response = await onboardingApiClient.get(url);
    return response.data;
  },
};
